import { useEffect, useRef, useState } from "react";

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Smoothly animate a number from its previous value to `target` whenever
 * `target` changes (and once `active` becomes true). Eased, rAF-driven,
 * honours prefers-reduced-motion.
 */
export function useCountUp(target: number, active = true, duration = 900) {
  const [value, setValue] = useState(active && prefersReduced ? target : 0);
  const fromRef = useRef(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!active) return;
    if (prefersReduced) {
      setValue(target);
      fromRef.current = target;
      return;
    }
    // Start from whatever is CURRENTLY displayed (fromRef tracks it live), so an
    // animation interrupted by a new target continues from where it visually is
    // instead of snapping back to the last completed value.
    const from = fromRef.current;
    const start = performance.now();
    cancelAnimationFrame(rafRef.current ?? 0);

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const current = from + (target - from) * eased;
      fromRef.current = current;
      setValue(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current ?? 0);
  }, [target, active, duration]);

  return value;
}
